import { profileTable, avatarBucket } from '$lib/supabase';
import type {
	ProfileAttrs,
	CommunityArray,
	SkillsArray,
	OffersArray,
	NeedsArray
} from './profile.type';
import { validate as uuidValidate } from 'uuid';
import { profileStore, profiles, profileLoading } from '.';
import { viewProfileStore } from '$lib/components/profileView/viewProfile.store';
import { supabaseClient } from '$lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import avatars from '$lib/components/common/AvatarLibrary.json';
import { get } from 'svelte/store';
import { user } from '$lib/user/user.store';
import { SecureSanitizer } from '$lib/utils/secureSanitizer';
import DOMPurify from 'dompurify';

const enableLogs = false;

// const GET_COL_SET = '*';

const GET_LIMITED_COL_SET = [
	'username',
	'realname',
	'avatar_url',
	'member',
	'id',
	'website',
	'timezone',
	'role',
	'availability',
	'bio',
	'persona',
	'twitter',
	'linkedin',
	'github',
	'communities',
	'skills',
	'profileredirects',
	'redirect',
	'wallet',
	'localbloom',
	'localbloom_systemname',
	'placename',
	'needs',
	'offers',
	'system_role',
	'member_app_in_progress'
].join(', ');

const GET_VERY_LIMITED_COL_SET = [
	'username',
	'realname',
	'avatar_url',
	'bio',
	'persona',
	'skills',
	'localbloom',
	'needs',
	'offers'
].join(', ');

// note, user comes from \lib\user\user.store.ts

// export async function post(request) {
// 	const { email } = request.body;

// 	const { data, error } = await supabaseClient.auth.api.getUserByEmail(email);

// 	if (error) {
// 		return {
// 			status: 500,
// 			body: { error: 'Internal Server Error' }
// 		};
// 	}

// 	return {
// 		status: 200,
// 		body: { exists: !!data } // Return true if email exists, false otherwise
// 	};
// }

/**
 * Checks if the email exists in the beta_access table.
 * @param email The email to check.
 * @returns A Promise<boolean> indicating whether the email exists.
 */
export const checkEmailHasBetaAccess = async (email: string): Promise<boolean> => {
	try {
		const { count, error } = await supabaseClient
			.from('beta_access')
			.select('*', { count: 'exact' })
			.eq('email', email);

		if (error) {
			console.error('Supabase error:', error.message);
			return false; // Explicitly return false or handle the error
		}

		// If count is greater than 0, email exists in beta_access table
		return count > 0;
	} catch (error) {
		console.error('An error occurred:', error);
		return false;
	}
};

/**
 * loginCheck - checks if user already exists in db
 *
 * @returns { Promise<boolean> } - true if the profile with the given wallet address or email exists, false otherwise
 *
 */
export const loginCheck = async (wallet: any, email: any): Promise<boolean> => {
	const { data, error } = await profileTable('profiles')
		.select('id')
		.or(`wallet.eq.${wallet}` as any, `email.eq.${email}` as any)
		.single();

	if (error) {
		console.error('Error checking profile:', error.message);
		return false;
	}

	return !!data; // Returns true if data is not null, indicating a profile with the given wallet address or email exists
};

export const getUserProfile = (id: string) =>
	profileTable('profiles').select(GET_LIMITED_COL_SET).eq('id', id).single();

export const getCurrUserProfile = async () => {
	const currentUser = get(user);
	if (!currentUser?.id) {
		throw new Error('User not available or missing ID');
	}
	return getUserProfile(currentUser.id);
};

export const getUsernamesAll = async () => {
	try {
		const { data, error } = await profileTable('profiles')
			.select('username, id') // Add id to the select
			.neq('username', 'anonymous'); // Exclude "anonymous";

		if (error) {
			throw new Error(`Error fetching usernames: ${error.message}`);
		}

		if (!data) {
			throw new Error('No data received while fetching usernames.');
		}
		// console.log('data in api', data);
		return { data: data || [], error: null };
	} catch (error) {
		console.error('Error fetching usernames:', error.message);
		return { data: [], error };
	}
};

/**
 * getLocationProfiles - Gets the real name of all profiles with a given localbloom
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getLocationProfiles = async (page: number, pageSize: number, location: string) => {
	// console.log('in getLocationProfiles', location);
	const { data, error: downloadError } = await profileTable('profiles')
		// TODO: need wallet here unless we can just store that in privy and use member id as the connecting factor, so we don't risk exposing their wallet address
		.select('id, realname, username, wallet')
		.eq('localbloom', location)
		.range(page * pageSize, (page + 1) * pageSize - 1);
	profiles.update((profile) => {
		for (let i = 0; i < data.length; i++) {
			profile.push(data[i]);
		}
		if (downloadError) {
			throw downloadError;
		}
		// console.log('profile after api update', profile);
		return profile;
	});
};

// the profiles call for locallead writing a rewards claim
/**
 * getLocationProfiles - Gets the real name of all profiles with a given localbloom
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getLocationMembers = async (location: string) => {
	const query = profileTable('profiles')
		.select('realname, wallet, id')
		.not('wallet', 'is', null) // Only return profiles with non-null wallet
		.not('wallet', 'eq', ''); // Exclude empty string wallets

	// Add conditional filter based on location
	if (location === 'Planet Earth') {
		query.eq('globalteam', true);
	} else {
		query.eq('localbloom', location);
	}

	const { data, error: downloadError } = await query;

	if (downloadError) {
		throw downloadError;
	}

	return data;
};

export const getAuthorUsername = async (id: string) => {
	const result = await profileTable('profiles').select('username').eq('id', id).single();
	return result?.data?.username ?? '';
};

export const getAuthorRole = async (id: string) => {
	const result = await profileTable('profiles').select('role').eq('id', id).single();
	return result?.data?.role ?? '';
};

// export const getAuthorAvatar_url = async (id: string) =>
// 	profileTable('profiles').select('avatar_url').eq('id', id);

export const getAuthorAvatar_url = async (id: string) => {
	try {
		const { data, error } = await profileTable('profiles')
			.select('avatar_url')
			.eq('id', id)
			.single();

		if (error) {
			// console.error('Supabase error:', error.message);
			// error;
		}
		// console.log('data in getAUthorAvatar_url', data);
		// Check if data is not null and contains the avatar_url property
		if (data && data.avatar_url) {
			const avatarUrl = data.avatar_url;
			// console.log('Avatar URL:', avatarUrl);
			return avatarUrl; // Return the avatar URL as a string
		} else {
			// console.error('Avatar URL not found in data:', data);
			return ''; // Return an empty string or handle the case when the avatar URL is not available.
		}
	} catch (e) {
		console.error('An error occurred:', e);
		throw e;
	}
};

export const getAuthorAvatar = async (url: string) => {
	// console.log('avatar getter is running');
	// console.log('url is correct inside AuthorAvatar', url);
	// this is showing as undefined

	// Handle null/undefined/empty URLs
	if (!url || url.trim() === '') {
		// Return a default avatar or throw an error
		throw new Error('No avatar URL provided');
	}

	// Remove leading slash to prevent double slash issues
	let cleanUrl = url.startsWith('/') ? url.substring(1) : url;

	// Check if this is a static avatar file from /img/avatars/
	if (cleanUrl.startsWith('img/avatars/')) {
		// Return the static file path directly - no need to download from storage
		return `/${cleanUrl}`;
	}

	try {
		const { data, error: downloadError } = await avatarBucket('avatars').download(cleanUrl, {
			transform: {
				width: 48,
				height: 48
			}
		});
		// console.log('data', data);
		if (downloadError) {
			throw downloadError;
		}

		return URL.createObjectURL(data);
	} catch (error) {
		console.error('Error downloading avatar:', error);
		// Return a fallback avatar or re-throw the error
		throw error;
	}
};

// old version - replaced bc one user's localbloom was being set to null/empty
// export const updCurrUserProfile = async (attrs: ProfileAttrs) =>
// 	profileTable('profiles').upsert({ ...attrs, id: getUser().id, updated_at: new Date() });

export const updCurrUserProfile = async (attrs: ProfileAttrs) => {
	const currentUser = get(user);
	if (!currentUser?.id) {
		throw new Error('User not available or missing ID');
	}
	const userId = currentUser.id;

	// Get current profile to check existing values
	const { data: currentProfile } = await profileTable('profiles')
		.select('localbloom, wallet')
		.eq('id', userId)
		.single();

	// If localbloom is being set to null/empty, check the current value
	if ('localbloom' in attrs && (!attrs.localbloom || attrs.localbloom.trim() === '')) {
		if (currentProfile?.localbloom) {
			// Prevent overwriting existing value with null/empty
			delete attrs.localbloom;
		}
	}

	// NEW: Protect wallet from being overwritten with null/empty
	if ('wallet' in attrs && (!attrs.wallet || attrs.wallet.trim() === '')) {
		if (currentProfile?.wallet) {
			// Prevent overwriting existing wallet address with null/empty
			delete attrs.wallet;
		}
	}

	return profileTable('profiles')
		.update({ ...attrs, updated_at: new Date() })
		.eq('id', userId);
};

/**
 * getProfileByUsername - Gets user by username
 *
 * @param <string>username - the member's username
 * @returns { data, error } where data is a single supabase post table object matching the slug
 *
 * TODO: better validation of ID and return object
 *
 */
export const getProfileByUsername = (username: string) =>
	profileTable('profiles').select(GET_LIMITED_COL_SET).eq('username', username).single();
// TODO: NOTE: VERY IMPORTANT - THIS GETS THE WHOLE ROW, WHICH MIGHT CONTAIN THEIR WALLET INFO, UNLESS WE CAN JUST STORE THAT IN PRIVY. MAKE SURE TO CHECK WITH DUSTIN BEFORE PUSH TO PROD WITH WALLET INFO AND EMAIL ADDRESS. ALSO NOTE - USERNAMES ARENT ENFORCED TO BE UNIQUE, YOU WILL NEED TO REPLACE THIS WITH ID SOON

/**
 * getIdByUsername - Gets id by given username
 *
 * @param <string>id - the member's id
 * @returns { data, error } where data is a single supabase post table object matching the slug
 *
 * TODO: better validation of ID and return object
 *
 */
export const getIdByUsername = async (username: string) => {
	try {
		// console.log('Received username:', username);

		const { data, error } = await profileTable('profiles')
			.select('id')
			.eq('username', username)
			.maybeSingle();

		// console.log('getIdByUsername', data, error);
		if (error) {
			return false;
		}

		return { data, error: null };
	} catch (error) {
		console.error('Error fetching profile data:', error.message);
		return { data: null, error };
	}
};

/**
 * getUsernameByRealname - Gets username by user's display name
 *
 * @param <string>username - the URL slug matching the post
 * @returns {string | null} where data is a single supabase post table object matching the slug
 *
 * TODO: better validation of ID and return object
 *
 */
export const getUsernameByRealname = async (realname: string) => {
	try {
		// console.log('Received id:', id);

		const { data, error } = await profileTable('profiles')
			.select('username')
			.eq('realname', realname)
			.maybeSingle();

		// console.log('getUsernameByRealname', data, error);
		if (error) {
			return false;
		}

		return data?.username || null;
	} catch (error) {
		console.error('Error fetching profile data:', error.message);
		return { data: null, error };
	}
};

/**
 * getUsernameById - Gets username by given id
 *
 * @param <string>username - the URL slug matching the post
 * @returns { data, error } where data is a single supabase post table object matching the slug
 *
 * TODO: better validation of ID and return object
 *
 */
export const getUsernameById = async (id: string) => {
	try {
		// console.log('Received id:', id);

		const { data, error } = await profileTable('profiles')
			.select('username')
			.eq('id', id)
			.maybeSingle();

		// console.log('getUsernameById', data, error);
		if (error) {
			return false;
		}

		return { data, error: null };
	} catch (error) {
		console.error('Error fetching profile data:', error.message);
		return { data: null, error };
	}
};

// this api call is based on how we are getting the location data in [location] routes

// Get a profile with the given username (example: nwagobo) and load it into the store
export const getViewProfile = async (profile: ProfileAttrs) => {
	if (!profile.username) {
		console.error('Username is undefined');
		return;
	}
	try {
		const { data /* error variable implied*/ } = await getProfileByUsername(profile.username);
		if (data.avatar_url) {
			// Clean up double slashes except after protocol (global)
			if (typeof data.avatar_url === 'string') {
				data.avatar_url = data.avatar_url.replace(/([^:]\/){2,}/g, '$1');
			}
			// If it's a static avatar, just use the path as-is
			if (data.avatar_url.startsWith('/img/avatars/')) {
				// Do nothing, use as-is
			} else {
				// Otherwise, fetch from Supabase Storage
				data.avatar_url = await getAvatar(data.avatar_url);
			}
		}
		viewProfileStore.set(data);
	} catch (error) {
		if (error instanceof TypeError) {
			/* ignored as the user is signed out*/
		} else console.log('profile.api.ts:getViewProfile:error', error);
	}
};

export const updCurrUserAvatar = async (id: string, fileOrPath: File | string) => {
	const currentUser = get(user);
	if (!currentUser?.id) {
		throw new Error('User not available or missing ID');
	}

	let filePath: string;

	if (fileOrPath instanceof File) {
		// Handle file upload case
		const fileExt = fileOrPath.name.split('.').pop();
		const fileName = `${id}${Math.random()}.${fileExt}`;
		filePath = fileName;

		const { error: uploadError } = await avatarBucket('avatars').upload(filePath, fileOrPath);
		if (uploadError) {
			throw uploadError;
		}
	} else {
		// Handle random avatar path case
		filePath = fileOrPath;
	}

	return await profileTable('profiles')
		.update({ avatar_url: filePath })
		.eq('id', currentUser.id)
		.select();
};

export const ensureDefaultAvatar = async (userId: string) => {
	try {
		const { data: profile } = await profileTable('profiles')
			.select('avatar_url')
			.eq('id', userId)
			.single();

		// Only set random avatar if they still have the default avatar
		if (profile?.avatar_url === 'avatar-default.svg') {
			const randomAvatar = avatars[Math.floor(Math.random() * avatars.length)];

			// Update the profile with the new avatar
			const { data, error: updateError } = await profileTable('profiles')
				.update({ avatar_url: randomAvatar })
				.eq('id', userId)
				.select()
				.single();

			if (updateError) {
				console.error('Error setting random avatar:', updateError);
				return null;
			}

			return data?.[0]?.avatar_url || null;
		}

		return profile?.avatar_url;
	} catch (error) {
		console.error('Failed to ensure default avatar:', error);
		return null;
	}
};

// these are for the 'view members' page, to search for members who have search term offers and needs

/**
 * searchSkills - Gets all profiles with given offer
 *
 * @param <number>limit - no parameters
 * @param local_bloom: string | null
 * @returns { data, error } where data is a list of supabase profile table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const searchSkills = async (
	page: number,
	pageSize: number,
	searchQuery: string,
	localBloom: string | null
) => {
	try {
		// console.log(`Searching for: ${searchQuery}, Page: ${page}, PageSize: ${pageSize}`);

		// Base query setup
		let query = supabaseClient
			.from('profiles')
			.select('id, username, skills, localbloom')
			.limit(pageSize)
			.range(page * pageSize, (page + 1) * pageSize - 1);

		// Add filter for localBloom if provided
		if (localBloom) {
			query = query.eq('localbloom', localBloom);
		}

		query = query.filter('skills', 'cs', `[{"skill": "${searchQuery}"}]`);

		// console.log('Executing query:', query);

		// Execute the query
		const { data, error } = await query;

		if (error) {
			console.error('Error fetching profiles:', error.message);
			return { data: [], error };
		}

		// console.log(`Results found: ${data ? data.length : 0}`);

		if (data && data.length > 0) {
			console.log('First result:', JSON.stringify(data[0], null, 2));
		} else {
			console.log('No results found');
		}

		return { data: data || [], error: null };
	} catch (error) {
		console.error('Error fetching profiles:', error.message);
		return { data: [], error: error.message };
	}
};

// .ilike('skills', query)

/**
 * searchOffers - Searches profiles with given offers
 *
 * @param page - Page number for pagination
 * @param pageSize - Number of items per page
 * @param query - Search query for offers
 * @param localBloom: string | null
 * @returns { data, error } where data is a list of profiles matching the search
 */
export const searchOffers = async (
	page: number,
	pageSize: number,
	query: string,
	localBloom: string | null
) => {
	try {
		// console.log(`Searching for offers: ${query}, Page: ${page}, PageSize: ${pageSize}`);
		const { data, error } = await supabaseClient.rpc('search_offers', {
			search_query: query,
			p_limit: pageSize,
			p_offset: page * pageSize,
			local_bloom: localBloom
		});

		if (error) {
			console.error('Error fetching profiles with offers:', error.message);
			return { data: [], error };
		}

		// console.log(`Results found for offers: ${data ? data.length : 0}`);

		// if (data && data.length > 0) {
		// 	console.log('First result:', JSON.stringify(data[0], null, 2));
		// } else {
		// 	console.log('No results found for offers');
		// }

		return { data: data || [], error: null };
	} catch (error) {
		console.error('Error fetching profiles with offers:', error.message);
		return { data: [], error: error.message };
	}
};

/**
 * searchNeeds - Searches profiles with given needs
 *
 * @param page - Page number for pagination
 * @param pageSize - Number of items per page
 * @param query - Search query for needs
 * @param localBloom: string | null
 * @returns { data, error } where data is a list of profiles matching the search
 */
export const searchNeeds = async (
	page: number,
	pageSize: number,
	query: string,
	localBloom: string | null
) => {
	try {
		// console.log(`Searching for needs: ${query}, Page: ${page}, PageSize: ${pageSize}`);
		const { data, error } = await supabaseClient.rpc('search_needs', {
			search_query: query,
			p_limit: pageSize,
			p_offset: page * pageSize,
			local_bloom: localBloom
		});

		if (error) {
			console.error('Error fetching profiles with needs:', error.message);
			return { data: [], error };
		}

		// console.log(`Results found for needs: ${data ? data.length : 0}`);

		// if (data && data.length > 0) {
		// 	console.log('First result:', JSON.stringify(data[0], null, 2));
		// } else {
		// 	console.log('No results found for needs');
		// }

		return { data: data || [], error: null };
	} catch (error) {
		console.error('Error fetching profiles with needs:', error.message);
		return { data: [], error: error.message };
	}
};

// export const searchOffers = async (query: string) => {
// 	const { data, error } = await profileTable('profiles')
// 		.select('offers')
// 		.textSearch('offers:ilike', query, { config: 'english' });
// 	if (error) {
// 		error;
// 	}

// 	return data;
// };

// export const searchNeeds = async (query: string) => {
// 	const { data, error } = await profileTable('profiles')
// 		.select('needs')
// 		.textSearch('needs:ilike', query, { config: 'english' });
// 	if (error) {
// 		error;
// 	}

// 	return data;
// };

// // get the uuid for the user whose realname name is...
// export const getAvatar_url = async (realname: string) => {
// 	return await profileTable('profiles')
// 		.select(GET_COL_SET)
// 		.eq('realname', realname)
// 		.select('avatar_url');
// };

export const getAvatar = async (url: string | null | undefined) => {
	if (!url) {
		return;
	}
	const { data, error: downloadError } = await avatarBucket('avatars').download(url, {
		transform: {
			width: 200,
			height: 200
		}
	});
	if (downloadError) {
		throw downloadError;
		// return;
	}

	return URL.createObjectURL(data);
};

export const getAvatarInPost = async (url: string) => {
	const { data, error: downloadError } = await avatarBucket('avatars').download(url, {
		transform: {
			width: 100,
			height: 100
		}
	});
	if (downloadError) {
		throw downloadError;
	}

	return URL.createObjectURL(data);
};

export const boolIsMember = async () => {
	const currentUser = get(user);
	if (!currentUser) {
		return false; // user isn't logged in
	}

	const { data, error } = await getCurrUserProfile();
	if (error) return false;
	if (data) return data.member;
	else return false; // didn't get data
};

/**
 *	setProfileUsername():  updates the Username for the current user's profile
 * @param id <UUIDv4> ID of the user to update username for
 * @param username <string>:  The username for the user
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const setProfileUsername = async (id: string, username: string) => {
	const valid = uuidValidate(id);
	// Username should be plain text only
	const CLEAN = SecureSanitizer.sanitizeText(username);

	if (valid) {
		// First get the current value
		const { data: currentProfile } = await profileTable('profiles')
			.select('username')
			.match({ id: id })
			.single();

		// If there's an existing value and the new value would be empty/null, abort
		if (currentProfile?.username && (!username || username.trim() === '')) {
			if (enableLogs) console.log('setProfileUsername: Prevented emptying existing username value');
			return { success: false, message: 'Cannot empty existing username' };
		}

		const { data: existingUsernames, error: selectError } = await profileTable('profiles')
			.select()
			.match({ username: CLEAN });

		if (selectError) {
			return { success: false, message: 'Failed to check existing usernames' };
		}

		if (existingUsernames && existingUsernames.length > 0) {
			return { success: false, message: 'Username is already taken' };
		}

		const { data: updateData, error: updateError } = await profileTable('profiles')
			.update({ username: CLEAN })
			.match({ id: id })
			.select();

		if (updateError) {
			return { success: false, message: 'Failed to update username' };
		}
		return { success: true, data: updateData };
	} else {
		return { success: false, message: 'Invalid user ID' };
	}
};

/**
 *	setProfileRealname():  updates the User's realname for the current user's profile
 * @param id <UUIDv4> ID of the user to update realname for
 * @param realname <string>:  The realname for the user
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const setProfileRealname = async (id: string, realname: string) => {
	if (enableLogs) console.log('setProfileRealname:ID', id);
	const valid = uuidValidate(id);
	// Realname should be plain text only
	const CLEAN = SecureSanitizer.sanitizeText(realname);

	if (valid) {
		// First get the current value
		const { data: currentProfile } = await profileTable('profiles')
			.select('realname')
			.match({ id: id })
			.single();

		// If there's an existing value and the new value would be empty/null, abort
		if (currentProfile?.realname && (!realname || realname.trim() === '')) {
			if (enableLogs) console.log('setProfileRealname: Prevented emptying existing realname value');
			return false;
		}

		if (enableLogs) console.log('setProfileRealname:ID:valid');
		const { data, error } = await profileTable('profiles')
			.update({ realname: CLEAN })
			.match({ id: id })
			.select();

		if (enableLogs) console.log('setProfileRealname result:', { data, error });

		if (error) {
			return false;
		}
		return { data, error: null };
	} else {
		if (enableLogs) console.log('setProfileRealname:ID:invalid');
		return false;
	}
};

/**
 * setLocalBloom ; Sets the self-defined localbloom of the member, distinct from localbloom which may be larger or smaller in scale
 *
 * @param id <string> The UUID of the post to update
 * @param localbloom <string> self-defined place the member lives
 * @returns <PostgrestFilterBuilder> | <boolean>false if error
 *
 * TODO: better return object error handling
 */
export const setProfileLocalBloom = async (id: string, localbloom: string) => {
	// console.log('setProfileLocalBloom:', id, localbloom);
	const valid = uuidValidate(id);

	if (valid) {
		// First get the current value
		const { data: currentProfile } = await profileTable('profiles')
			.select('localbloom')
			.match({ id: id })
			.single();

		// If there's an existing value and the new value would be empty/null, abort
		if (currentProfile?.localbloom && (!localbloom || localbloom.trim() === '')) {
			if (enableLogs)
				console.log('setProfileLocalBloom: Prevented emptying existing localbloom value');
			return false;
		}

		const CLEAN = SecureSanitizer.sanitize(localbloom);
		// console.log('setProfileLocalBloom: Valid UUID');
		const retVal = profileTable('profiles')
			.update({ localbloom: CLEAN })
			.match({ id: id })
			.select();

		if (enableLogs) console.log(retVal);
		return retVal;
	} else {
		if (enableLogs) console.log('setProfileLocalBloom: ID not a valid UUID');
		return false;
	}
};

export const setProfileLocalBloomSystemName = async (id: string, systemname: string) => {
	// console.log('setProfileLocalBloomSystemName:', id, systemname);
	const valid = uuidValidate(id);

	if (valid) {
		const CLEAN = SecureSanitizer.sanitize(systemname);
		// console.log('setProfileLocalBloom: Valid UUID');
		const retVal = profileTable('profiles')
			.update({ localbloom_systemname: CLEAN })
			.match({ id: id })
			.select();

		if (enableLogs) console.log(retVal);
		return retVal;
	} else {
		if (enableLogs) console.log('setProfileLocalBloomSytemName: ID not a valid UUID');
		return false;
	}
};

// /**
//  * setPlaceName ; Sets the self-defined placename of the member, distinct from localbloom which may be larger or smaller in scale
//  *
//  * @param id <string> The UUID of the post to update
//  * @param placename <string> self-defined place the member lives
//  * @returns <PostgrestFilterBuilder> | <boolean>false if error
//  *
//  * TODO: better return object error handling
//  */
// export const setProfilePlaceName = async (id: string, placename: string) => {
// 	// console.log('setProfilePlaceName:', id, placename);
// 	const valid = uuidValidate(id);

// 	if (valid) {
// 		const CLEAN = DOMPurify.sanitize(placename);
// 		// console.log('setProfilePlaceName: Valid UUID');
// 		const retVal = profileTable('profiles').update({ placename: CLEAN }).match({ id: id }).select();

// 		if (enableLogs) console.log(retVal);
// 		return retVal;
// 	} else {
// 		if (enableLogs) console.log('setProfilePlaceName: ID not a valid UUID');
// 		return false;
// 	}
// };

/**
 *	setProfileRole():  updates the Role for the current user's profile
 * @param id <UUIDv4> ID of the user to update role for
 * @param role <string>:  The community role for the user (are they a graphic designer, a soil scientist, a local organizer...)
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const setProfileRole = async (id: string, role: string) => {
	if (enableLogs) console.log('ID', id);
	const valid = uuidValidate(id);
	const CLEAN = SecureSanitizer.sanitize(role);

	if (valid) {
		if (enableLogs) console.log('Valid UUID');
		const retVal = profileTable('profiles').update({ role: CLEAN }).match({ id: id }).select();

		if (enableLogs) console.log(retVal);
		return retVal;
	} else {
		if (enableLogs) console.log('ID not a valid UUID');
		return false;
	}
};

/**
 *	setProfileTimezone():  updates the Timezone for the current user's profile
 * @param id <UUIDv4> ID of the user to update timezone for
 * @param timezone <string>:  The timezone for the user
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const setProfileTimezone = async (id: string, timezone: string) => {
	if (enableLogs) console.log('ID', id);
	const valid = uuidValidate(id);
	const CLEAN = SecureSanitizer.sanitize(timezone);

	if (valid) {
		if (enableLogs) console.log('Valid UUID');
		const retVal = profileTable('profiles').update({ timezone: CLEAN }).match({ id: id }).select();

		if (enableLogs) console.log(retVal);
		return retVal;
	} else {
		if (enableLogs) console.log('ID not a valid UUID');
		return false;
	}
};

/**
 *	setProfileAvailability():  updates the Availability for the current user's profile
 * @param id <UUIDv4> ID of the user to update availability for
 * @param availability <number>:  The number of hours per week the user is available to contribute to regenerative actions
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const setProfileAvailability = async (id: string, availability: number) => {
	if (enableLogs) console.log('setProfileAvailability:ID', id);
	const valid = uuidValidate(id);

	if (valid) {
		if (enableLogs) console.log('setProfileAvailability:ID:Valid');
		const retVal = profileTable('profiles')
			.update({ availability: availability })
			.match({ id: id })
			.select();

		if (enableLogs) console.log(retVal);
		return retVal;
	} else {
		if (enableLogs) console.log('setProfileAvailability:ID:Invalid');
		return false;
	}
};

/**
 *	setProfileBio():  updates the Bio for the current user's profile
 * @param id <UUIDv4> ID of the user to update bio for
 * @param bio <string>: A short bio of the member, or anything they want to say there
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const setProfileBio = async (id: string, bio: string) => {
	if (enableLogs) console.log('setProfileBio', id);
	const valid = uuidValidate(id);

	// Bio should be plain text only
	const CLEAN = SecureSanitizer.sanitizeText(bio);

	if (valid) {
		if (enableLogs) console.log('setProfileBio - Valid UUID');
		const retVal = await profileTable('profiles').update({ bio: CLEAN }).match({ id: id }).select();

		if (enableLogs) console.log(retVal);
		return retVal;
	} else {
		if (enableLogs) console.log('setProfileBio - ID not a valid UUID');
		return false;
	}
};

/**
 *	setProfileWallet():  safely updates the wallet address for the current user's profile
 * @param id <UUIDv4> ID of the user to update wallet for
 * @param wallet <string>: the member's bloom nft wallet address
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * This function safely updates wallet addresses and includes validation
 */
export const setProfileWallet = async (id: string, wallet: string) => {
	if (enableLogs) console.log('setProfileWallet', id);
	const valid = uuidValidate(id);
	const CLEAN = DOMPurify.sanitize(wallet);

	if (valid && CLEAN && CLEAN.trim() !== '') {
		if (enableLogs) console.log('setProfileWallet - Valid UUID and wallet address');
		try {
			const retVal = profileTable('profiles').update({ wallet: CLEAN }).match({ id: id }).select();
			if (enableLogs) console.log(retVal);
			return retVal;
		} catch (e) {
			console.log('failed to update wallet');
			return false;
		}
	} else {
		if (enableLogs) console.log('setProfileWallet - Invalid UUID or empty wallet address');
		return false;
	}
};

/**
 *	safeUpdateWallet():  safely updates wallet address only if the new value is valid
 * @param id <UUIDv4> ID of the user to update wallet for
 * @param newWallet <string>: the new wallet address
 * @returns <boolean> true if successful, false if error or invalid
 *
 * This function prevents overwriting valid wallet addresses with invalid ones
 */
export const safeUpdateWallet = async (id: string, newWallet: string): Promise<boolean> => {
	try {
		// Validate inputs
		if (!uuidValidate(id) || !newWallet || newWallet.trim() === '') {
			console.log('safeUpdateWallet - Invalid inputs');
			return false;
		}

		// Check current wallet address
		const { data: currentProfile } = await profileTable('profiles')
			.select('wallet')
			.eq('id', id)
			.single();

		// If current wallet exists and new wallet is empty/null, don't update
		if (currentProfile?.wallet && (!newWallet || newWallet.trim() === '')) {
			console.log('safeUpdateWallet - Preventing overwrite of existing wallet with empty value');
			return false;
		}

		// Validate wallet address format (basic check)
		if (!newWallet.startsWith('0x') || newWallet.length !== 42) {
			console.log('safeUpdateWallet - Invalid wallet address format');
			return false;
		}

		// Update wallet address
		const { error } = await profileTable('profiles')
			.update({ wallet: newWallet.trim() })
			.eq('id', id);

		if (error) {
			console.log('safeUpdateWallet - Database error:', error);
			return false;
		}

		console.log('safeUpdateWallet - Successfully updated wallet address');
		return true;
	} catch (error) {
		console.log('safeUpdateWallet - Unexpected error:', error);
		return false;
	}
};

/**
 *	setProfilePersona():  updates the Persona for the current user's profile
 * @param id <UUIDv4> ID of the user to update persona for
 * @param persona <string>: Member's persona: are they mostly doing backyard/neighborhood actions or applying specifically their professional skillset
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const setProfilePersona = async (id: string, persona: string) => {
	if (enableLogs) console.log('Profile::Persona::ID', id);
	const valid = uuidValidate(id);
	const CLEAN = SecureSanitizer.sanitize(persona);

	if (valid) {
		if (enableLogs) console.log('Profile::Persona::UUID Valid');
		const retVal = profileTable('profiles').update({ persona: CLEAN }).match({ id: id }).select();

		if (enableLogs) console.log(retVal);
		return retVal;
	} else {
		if (enableLogs) console.log('Profile::Persona::UUID Invalid');
		return false;
	}
};

/**
 *	setProfileSkills():  updates the skills for the current user's profile
 * @param id <string> The UUID of the profile to update
 * @param skills <SkillsArray>: JSON array of Member's skillsets: graphic design, conflict resolution, agroforestry, etc
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const setProfileSkills = async (id: string, skills: SkillsArray) => {
	if (enableLogs) console.log('setSkills:', id, skills);
	const valid = uuidValidate(id);
	const cleanString = SecureSanitizer.sanitize(JSON.stringify(skills));
	const CLEAN = JSON.parse(cleanString);

	if (valid) {
		if (enableLogs) console.log('setSkills: Valid UUID');
		const retVal = profileTable('profiles').update({ skills: CLEAN }).match({ id: id }).select();

		if (enableLogs) console.log(retVal);
		return retVal;
	} else {
		if (enableLogs) console.log('setSkills: ID not a valid UUID');
		return false;
	}
};

/**
 *	setProfileNeeds():  updates the needs for the current user's profile
 * @param id <string> The UUID of the profile to update
 * @param needs <NeedsArray>: JSON array of Member's needs: graphic design, conflict resolution, agroforestry lessons, etc
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const setProfileNeeds = async (id: string, needs: NeedsArray) => {
	if (enableLogs) console.log('setNeeds:', id, needs);
	const valid = uuidValidate(id);
	const cleanString = SecureSanitizer.sanitize(JSON.stringify(needs));
	const CLEAN = JSON.parse(cleanString);

	if (valid) {
		if (enableLogs) console.log('setNeeds: Valid UUID');
		const retVal = profileTable('profiles').update({ needs: CLEAN }).match({ id: id }).select();

		if (enableLogs) console.log(retVal);
		return retVal;
	} else {
		if (enableLogs) console.log('setNeeds: ID not a valid UUID');
		return false;
	}
};

/**
 *	setProfileOffers():  updates the offers for the current user's profile
 * @param id <string> The UUID of the profile to update
 * @param offers <OffersArray>: JSON array of Member's offers: graphic design, conflict resolution, agroforestry lessons, etc
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const setProfileOffers = async (id: string, offers: OffersArray) => {
	if (enableLogs) console.log('setOffers:', id, offers);
	const valid = uuidValidate(id);
	const cleanString = SecureSanitizer.sanitize(JSON.stringify(offers));
	const CLEAN = JSON.parse(cleanString);

	if (valid) {
		if (enableLogs) console.log('setOffers: Valid UUID');
		const retVal = profileTable('profiles').update({ offers: CLEAN }).match({ id: id }).select();

		if (enableLogs) console.log(retVal);
		return retVal;
	} else {
		if (enableLogs) console.log('setOffers: ID not a valid UUID');
		return false;
	}
};

/**
 * setProfileCommunities ; Sets user profile Communities object
 * @param id <string> The UUID of the profile to update
 * @param communities <CommunityArray> JSON community array object
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate profile ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setProfileCommunities = async (id: string, communities: CommunityArray) => {
	if (enableLogs) console.log('setCommunities:', id, communities);
	const valid = uuidValidate(id);
	const cleanString = SecureSanitizer.sanitize(JSON.stringify(communities));
	const CLEAN = JSON.parse(cleanString);

	if (valid) {
		if (enableLogs) console.log('setCommunities: Valid UUID');
		const retVal = profileTable('profiles')
			.update({ communities: CLEAN })
			.match({ id: id })
			.select();

		if (enableLogs) console.log(retVal);
		return retVal;
	} else {
		if (enableLogs) console.log('setCommunities: ID not a valid UUID');
		return false;
	}
};

/**
 *	setProfileTwitter():  updates the Twitter handle for the current user's profile
 * @param id <UUIDv4> ID of the user to update twitter for
 * @param twitter <string>: The user's twitter handle
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const setProfileTwitter = async (id: string, twitter: string) => {
	if (enableLogs) console.log('ID', id);
	const valid = uuidValidate(id);
	const CLEAN = SecureSanitizer.sanitize(twitter);

	if (valid) {
		if (enableLogs) console.log('Valid UUID');
		const retVal = profileTable('profiles').update({ twitter: CLEAN }).match({ id: id }).select();

		if (enableLogs) console.log(retVal);
		return retVal;
	} else {
		if (enableLogs) console.log('ID not a valid UUID');
		return false;
	}
};

/**
 *	setProfileLinkedin():  updates the Linkedin URL for the current user's profile
 * @param id <UUIDv4> ID of the user to update Linkedin for
 * @param linkedin <string>: The user's linkedin URL
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const setProfileLinkedin = async (id: string, linkedin: string) => {
	if (enableLogs) console.log('ID', id);
	const valid = uuidValidate(id);
	const CLEAN = SecureSanitizer.sanitize(linkedin);

	if (valid) {
		if (enableLogs) console.log('Valid UUID');
		const retVal = profileTable('profiles').update({ linkedin: CLEAN }).match({ id: id }).select();

		if (enableLogs) console.log(retVal);
		return retVal;
	} else {
		if (enableLogs) console.log('ID not a valid UUID');
		return false;
	}
};

/**
 *	setProfileGithub():  updates the Github URL for the current user's profile
 * @param id <UUIDv4> ID of the user to update github for
 * @param linkedgithubin <string>: The user's github handle
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const setProfileGithub = async (id: string, github: string) => {
	if (enableLogs) console.log('ID', id);
	const valid = uuidValidate(id);
	const CLEAN = SecureSanitizer.sanitize(github);

	if (valid) {
		if (enableLogs) console.log('Valid UUID');
		const retVal = profileTable('profiles').update({ github: CLEAN }).match({ id: id }).select();

		if (enableLogs) console.log(retVal);
		return retVal;
	} else {
		if (enableLogs) console.log('ID not a valid UUID');
		return false;
	}
};

// get the current users profile and load it into the store
export const getProfile = async () => {
	try {
		// Set loading state
		profileLoading.set(true);

		// Check if user store is available before calling getUser()
		const currentUser = get(user);
		if (!currentUser) {
			console.warn('User store is not available, skipping profile fetch');
			return;
		}

		const { data /* error variable implied*/ } = await getCurrUserProfile();

		if (!data) {
			throw new Error('No profile data received.');
		}

		if (data.avatar_url) {
			// Clean up double slashes except after protocol (global)
			if (typeof data.avatar_url === 'string') {
				data.avatar_url = data.avatar_url.replace(/([^:]\/){2,}/g, '$1');
			}
			// If it's a static avatar, just use the path as-is
			if (data.avatar_url.startsWith('/img/avatars/')) {
				// Do nothing, use as-is
			} else {
				// Otherwise, fetch from Supabase Storage
				data.avatar_url = await getAvatar(data.avatar_url);
			}
		}

		profileStore.set(data);
	} catch (error) {
		if (error instanceof TypeError) {
			console.warn('User is signed out. Profile data not fetched.');
		} else {
			console.error('Error fetching profile:', error);
		}
	} finally {
		// Clear loading state
		profileLoading.set(false);
	}
};

/**
 * getProfileIdByUsername: Retrieves the profile ID based on the provided username.
 * @param username <string> The username of the profile to retrieve.
 * @returns <string | null> The profile ID if found, otherwise null.
 */
export const getProfileIdByUsername = async (username: string): Promise<string | null> => {
	try {
		const { data, error } = await profileTable('profiles')
			.select('id')
			.eq('username', username)
			.single();

		if (error) {
			console.error('Error fetching profile ID:', error.message);
			return null;
		}

		return data?.id || null;
	} catch (error) {
		console.error('Error fetching profile ID:', error.message);
		return null;
	}
};

/**
 *	setProfileRedirects():  checks if the current user should see the profile redirect
 * 							  and decrements the number of remaining logins
 */
export const setProfileRedirects = async () => {
	profileStore.subscribe(async (_profile) => {
		// only set if we need to
		if (_profile.profileredirects > 0) {
			const redirects = _profile.profileredirects - 1;
			await profileTable('profiles')
				.update({ profileredirects: redirects })
				.match({ id: _profile.id });
		}
	});
};

/**
 *	setRedirect():  sets redirect in DB to determine if user should route to member page
 */
export const setRedirect = async (redirect: boolean) => {
	// const { data /* error variable implied*/ } = await getCurrUserProfile();
	const currentUser = get(user);
	if (!currentUser?.id) {
		throw new Error('User not available or missing ID');
	}
	const id = currentUser.id;
	// await profileTable('profiles').update({ redirect: redirect }).match({ id: data.id });
	await profileTable('profiles').update({ redirect: redirect }).match({ id: id }).select();
	// console.log('setRedirect', data.redirect, redirect);
	console.log('setRedirect', redirect);
};

// Utility function to shuffle an array
function shuffleArray(array: any[]) {
	for (let i = array.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[array[i], array[j]] = [array[j], array[i]];
	}
	return array;
}

/**
 * getProfiles - Gets all profiles
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getProfiles = async () => {
	// page: number, pageSize: number
	try {
		const { data, error } = await profileTable('profiles')
			.select(GET_VERY_LIMITED_COL_SET)
			.not('username', 'is', null) // Exclude null usernames
			.not('username', 'eq', '');
		// .range(page * pageSize, (page + 1) * pageSize - 1);

		if (error) {
			console.error('Supabase error:', error.message);
			return null; // Return null if there's an error
		}

		if (!data) {
			console.error('No data returned from Supabase');
			return null; // Return null if data is not found
		}

		// Shuffle the data if available
		const shuffledData = shuffleArray(data);
		profiles.set(shuffledData);

		// Returning the data in case we want to use it in the calling function
		return { data: shuffledData, error: null };
	} catch (err) {
		console.error('Error fetching profiles:', err);
		return null; // Handle any unexpected errors
	}
};
// BUG: data is possibly not working the same in the new upgrade

/**
 * getProfiles - Gets all profiles
 *
 * @param <number>limit - no parameters
 * @param localbloom - local bloom of current user
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getProfilesLocal = async (localbloom: string) => {
	try {
		let query;

		if (localbloom === 'Northeast US / Dawnlands') {
			// Special case for 'Northeast US / Dawnlands'
			query = profileTable('profiles')
				.select(GET_LIMITED_COL_SET)
				.or(
					'localbloom.eq.Northeast US / Dawnlands,localbloom.eq.Pollinator / Nomadic - Permatours'
				);
		} else {
			// Default case
			query = profileTable('profiles').select(GET_LIMITED_COL_SET).eq('localbloom', localbloom);
		}

		// Execute the query
		const { data, error } = await query;

		// Handle errors
		if (error) {
			console.error('Supabase error:', error.message);
			return { data: null, error };
		}

		// Shuffle data if required
		const shuffledData = data ? shuffleArray(data) : [];
		profiles.set(shuffledData);

		return { data: shuffledData, error: null };
	} catch (error) {
		console.error('Unexpected error fetching profiles:', error);
		return { data: null, error };
	}
};

/**
 *
 * @param url <string> - the Supabase URL string of the image to fetch
 * @returns <string> - a URL object string that can be used in an <img src={getImage('someImage')}> tag
 *
 * TODO: string validation
 *       better return object error handling
 */
export const checkIfValidAddress = async (wallet: string) => {
	try {
		// Fetch data from the profiles table
		const { data } = await profileTable('profiles').select('wallet').eq('wallet', wallet).single(); // Ensure only one row is returned

		// If data is null or undefined, the wallet is invalid
		if (!data) {
			return {
				valid: false,
				error:
					'Invalid wallet address - if you know this is the address of one of your Local Bloom members, you need to ask them to add it to their BloomNetwork.earth profile, before you are able to include it in a rewards claim.'
			};
		}

		// Address is valid if data is not null
		return { valid: true };
	} catch (error) {
		console.error('Error checking wallet address:', error.message);
		return { valid: false, error: 'Internal server error' };
	}
};

export const addUsers = async (emails: string[], inviter: string, inviterBloom: string) => {
	const results = [];

	for (const email of emails) {
		try {
			// Check if a profile already exists with this email
			const { data: existingProfile, error: fetchError } = await supabaseClient
				.from('profiles')
				.select('id')
				.eq('email', email.trim())
				.single(); // We expect one result if the profile exists

			if (fetchError && fetchError.code !== 'PGRST116') {
				// Handle unexpected errors during fetch (e.g., database issues)
				results.push({
					email,
					success: false,
					error: `Failed to fetch ${email}: ${fetchError.message}`
				});
				continue;
			}

			// Skip adding the profile if the email already exists
			if (existingProfile) {
				results.push({ email, success: true, message: `User already exists with email ${email}` });
				continue;
			}

			// Insert a new profile if it doesn't exist
			const { data, error } = await supabaseClient.from('profiles').insert({
				id: uuidv4(), // Generate a unique ID for each member
				email: email.trim(), // Trim email in case of extra spaces
				member: true,
				invited_by: inviter,
				localbloom: inviterBloom
			});

			if (error) {
				results.push({ email, success: false, error: `Failed to add ${email}: ${error.message}` });
			} else {
				results.push({ email, success: true, data });
			}
		} catch (error) {
			console.error(`Error adding member ${email}:`, error.message);
			results.push({ email, success: false, error: 'Internal server error' });
		}
	}

	return results;
};

/**
 * getProfileIdsByUsernames - Gets profile IDs for specific usernames
 * @param usernames Array of usernames to look up
 * @returns Array of profile IDs
 */
export const getProfileIdsByUsernames = async (usernames: string[]) => {
	try {
		const { data, error } = await profileTable('profiles')
			.select('id, username')
			.in('username', usernames);

		if (error) {
			console.error('Error fetching profile IDs:', error.message);
			return { data: [], error };
		}

		// Create a map of username to id
		const usernameToIdMap = {};
		data?.forEach((profile) => {
			usernameToIdMap[profile.username] = profile.id;
		});

		return { data: usernameToIdMap, error: null };
	} catch (error) {
		console.error('Error fetching profile IDs:', error.message);
		return { data: {}, error };
	}
};

/**
 * searchUsernames - Search usernames with pagination and filtering
 * @param searchTerm The search term to match against usernames
 * @param limit Maximum number of results to return
 * @returns Array of matching usernames
 */
export const searchUsernames = async (searchTerm: string, limit: number = 10) => {
	try {
		const { data, error } = await profileTable('profiles')
			.select('username')
			.ilike('username', `${searchTerm}%`) // Case-insensitive prefix match
			.neq('username', 'anonymous')
			.limit(limit);

		if (error) {
			console.error('Error searching usernames:', error.message);
			return { data: [], error };
		}

		return {
			data: data?.map((profile) => profile.username) || [],
			error: null
		};
	} catch (error) {
		console.error('Error searching usernames:', error.message);
		return { data: [], error };
	}
};
